import { Form, Formik, useFormikContext } from "formik";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H3 } from "../../../components/Heading";
import { Input } from "../../../components/Input";
import { Paragraph } from "../../../components/Paragraph";
import { TextArea } from "../../../components/TextArea";
import { validateForm } from "../../../utils/validationSchema";
import { useUpdateForm } from "../../../data/useUpdateForm";
import { showError } from "../../../utils/error-handling";
import { notify } from "../../../utils/notify";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateForm } from "../../../data/useCreateForm";
import { queryClient } from "../../../queryClient";
import Select, { Option } from "../../../components/Select";
import { useEffect, useMemo, useRef } from "react";
import { useBillingInfo } from "../../../data/useBillingInfo";

interface FormDetailsProps {
  initialValues: any;
  editMode: boolean;
}

type FormValues = {
  name: string;
  description: string;
  _type?: "individual" | "entity" | "";
};

const EnforceIndividualFormType = ({
  profilesEnabled,
}: {
  profilesEnabled: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useEffect(() => {
    if (!profilesEnabled && values._type !== "individual") {
      setFieldValue("_type", "individual");
    }
  }, [profilesEnabled, values._type, setFieldValue]);

  return null;
};

export const FormDetails: React.FC<FormDetailsProps> = ({
  initialValues,
  editMode,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { mutate: updateForm } = useUpdateForm();
  const { mutate: createForm, isLoading: isCreating } = useCreateForm();
  const createInProgressRef = useRef(false);

  const { data: billingData } = useBillingInfo();
  const billingInfo =
    billingData?.data?.results?.length > 0 ? billingData.data.results[0] : null;

  const profilesEnabled = Boolean(billingInfo?.profiles_enabled);

  const TYPE_OPTIONS: Option[] = useMemo(
    () => [
      { label: "Individual", value: "individual" },
      { label: "Entity", value: "entity" },
    ],
    [],
  );

  const handleSubmit = (values: any) => {
    const onSuccess = (res: any, message: string, redirectUrl?: string) => {
      if (res?.status?.toString().startsWith("2")) {
        queryClient.invalidateQueries({ queryKey: ["form"] });
        if (redirectUrl) navigate(redirectUrl);
        notify(message, { type: "success", position: "bottom-left" });
      }
    };

    const onError = (err: any, message: string) => showError(err, message);

    const safeValues = {
      ...values,
      _type:
        profilesEnabled && values?._type === "entity" ? "entity" : "individual",
    };

    if (editMode || id) {
      updateForm(
        { params: safeValues, id: Number(id) },
        {
          onError: (err) =>
            onError(err, "A problem occurred while updating form details."),
          onSuccess: (res) =>
            onSuccess(res, "Form details updated successfully!"),
        },
      );
    } else {
      if (createInProgressRef.current) return;

      createInProgressRef.current = true;
      createForm(safeValues, {
        onError: (err) => onError(err, "A problem occurred while creating form."),
        onSuccess: (res) =>
          onSuccess(
            res,
            "Form created successfully!",
            `/settings/forms/${res.data.id}/questions`,
          ),
        onSettled: () => {
          createInProgressRef.current = false;
        },
      });
    }
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        _type: profilesEnabled ? initialValues?._type ?? "" : "individual",
      }}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validateForm}
    >
      {({
        handleChange,
        handleSubmit: submit,
        values,
        errors,
        dirty,
        isValid,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form onSubmit={submit}>
          <EnforceIndividualFormType profilesEnabled={profilesEnabled} />

          <Flex
            bg="white"
            width="100%"
            gap="12"
            justifyContent="space-between"
            flexWrap="wrap"
            borderRadius="16px"
            pb={2}
          >
            <Box pr={2} flex={4} minWidth={400}>
              <H3 fontSize="2">Form Details</H3>
              <Paragraph fontWeight={500} color="gray.60" fontSize={1}>
                These details will display on the mobile app.
              </Paragraph>

              <Box>
                {profilesEnabled && (
                  <Box my={3}>
                    <Select
                      label="Type"
                      options={TYPE_OPTIONS}
                      value={
                        TYPE_OPTIONS.find(
                          (o) => o.value === (values as any)._type,
                        ) as any
                      }
                      onChange={(opt: any) =>
                        setFieldValue(
                          "_type",
                          (opt?.value ?? "individual") as
                            | "individual"
                            | "entity",
                        )
                      }
                      width="auto"
                      isDisabled={isSubmitting}
                    />
                  </Box>
                )}

                <Box my={3}>
                  <Input
                    onChange={handleChange}
                    value={(values as any).name}
                    placeholder="Form name"
                    label="Form Name"
                    name="name"
                    isRequired
                    hasError={Boolean((errors as any).name)}
                    errorMessage={(errors as any).name as string}
                  />
                </Box>

                <Box my={3}>
                  <TextArea
                    onChange={handleChange}
                    value={(values as any).description}
                    placeholder="Form description"
                    label="Description"
                    name="description"
                    rows={4}
                    isRequired
                    hasError={Boolean((errors as any).description)}
                    errorMessage={(errors as any).description as string}
                  />
                </Box>

                <Box mt={4}>
                  <Flex justifyContent="end">
                    <Button
                      size="large"
                      mb="0"
                      type="submit"
                      isDisabled={
                        !dirty ||
                        (dirty && !isValid) ||
                        (!editMode && !id && isCreating)
                      }
                    >
                      {editMode || id ? "Save" : "Create"}
                    </Button>
                  </Flex>
                </Box>
              </Box>
            </Box>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};
